<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- User info page -->
<template>
  <div v-if="user" class="user page">
    <div class="page-heading">
      <h1>{{ user.firstName }} {{ user.lastName }}</h1>
    </div>

    <div class="page-content">
      <!-- Permissions -->
      <section class="page-section permissions">
        <h4>Permissions</h4>

        <div v-for="permission of user.permissions" :key="permission.id" class="permission">
          <h5>{{ permission.name }}</h5>
          <p>{{ permission.description }}</p>
        </div>

        <a class="shaded" @click="showPermissionForm"><i class="fal fa-edit"></i> Edit</a>
      </section>

      <!-- Permission Form -->
      <av-modal :visible="shouldShowPermissionForm" @close="shouldShowPermissionForm = false">
        <div class="modal-header">Edit Permissions for {{ user.firstName }}</div>
        <div class="modal-content">
          <av-radio-group :modelValue="visilbilityPermission" @update:modelValue="editVisilbilityPermission">
            <av-radio-button :value="0">View Only</av-radio-button>
            <av-radio-button :value="1">Reviewer</av-radio-button>
          </av-radio-group>
          <br>
          <av-checkbox-group v-model="editedPermissions">
            <av-checkbox :value="2" class="primary">Admin</av-checkbox>
            <br><br>
            <av-checkbox :value="3" class="primary">Supervisor</av-checkbox>
          </av-checkbox-group>
        </div>
        <div class="modal-actions">
          <av-spinner v-if="syncing">{{ 'Updating user data' }}</av-spinner>
          <av-button v-if="!syncing" class="hollow" color="error" @click="shouldShowPermissionForm = false">Cancel</av-button>
          <av-button v-if="!syncing" @click="updatePermissions">Update</av-button>
        </div>
      </av-modal>

      <!-- Availability -->
      <section class="page-section availability" v-if="isReviewer">
        <h4>Availability</h4>
        <availability-form
          v-for="availability of sortedAvailability"
          :key="availability.id"
          :userId="user.id"
          :userAvailability="availability"
          @update-availability="updateAvailability"
        ></availability-form>
      </section>

      <!-- Blocked Off Times -->
      <section class="page-section blocked-times" v-if="isReviewer">
        <h4>Blocked Times</h4>
        <div class="blocked-time" v-for="blockedTime of user.blockedTimes" :key="blockedTime.id">
          <b>{{ blockedTime.title }}</b>
          <span>{{ formatTimeString(blockedTime.startTime, 'MMM do yyyy') }} - {{ formatTimeString(blockedTime.endTime, 'MMM do yyyy') }}</span>
          <a class="av-link error" @click="removeBlockedTime(blockedTime.id)"> Remove</a>
        </div>
        <div><a class="shaded" @click="showBlockedTimeForm"><i class="fal fa-plus-circle"></i> Add</a></div>

        <!-- Blocked Time Form -->
        <av-modal :visible="shouldShowBlockTimeform" @close="shouldShowBlockTimeform = false">
          <div class="modal-header">New Blocked Time</div>
          <div class="modal-content blocked-time-form">
            <label class="av-label">Title</label>
            <input class="av-input" type="text" v-model="newBlockedTime.title" />

            <label class="av-label">Select Date Range</label>
            <div class="datepicker"></div>
          </div>
          <div class="modal-actions">
            <av-button class="hollow error" @click="shouldShowBlockTimeform = false">Cancel</av-button>
            <av-button @click.once="createBlockedTime" :disabled="!isActive || newBlockedTime.title === '' || !newBlockedTime.startTime || !newBlockedTime.endTime">Save</av-button>
          </div>
        </av-modal>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import User from '../models/user'
import AvailabilityForm from '../components/AvailabilityForm.vue'
import Litepicker from 'litepicker'
import { format } from 'date-fns'
import BlockedTime from '../models/blockedTime'
import UserService from '../common/userService'
import UserAvailabilityByType from '../models/userAvailabilityByType'
import { cloneDeep } from 'lodash'
import UserAvailability from '../models/userAvailability'
import endOfDay from 'date-fns/fp/endOfDay/index.js'

export default defineComponent({
  name: 'User',

  data () {
    return {
      isActive: true,
      syncing: false,
      appointmentTypes: [] as Array<any>,

      /** Edited list of permissions to save */
      editedPermissions: [] as Array<number>,

      /** Information for new blocked time */
      newBlockedTime: {
        title: '',
        startTime: null as unknown as Date,
        endTime: null as unknown as Date
      } as BlockedTime,

      /** Indicator to show or hide the Blocked Time form */
      shouldShowBlockTimeform: false,

      /** Indicator to show or hide the permission form */
      shouldShowPermissionForm: false,

      /** The user to be edited */
      user: null as unknown as User,

      /** List of permissions */
      permissions: [
        {
          id: 0,
          code: 'VIEW',
          description: 'Allow viewing calendar entries',
          name: 'View Calendar',
          permissionValue: 1
        },
        {
          id: 1,
          code: 'REVU',
          description: 'Allow changing own calendar entries',
          name: 'Review',
          permissionValue: 2
        },
        {
          id: 2,
          code: 'ADMN',
          description: 'Allow managing users and system',
          name: 'Manage System',
          permissionValue: 4
        },
        {
          id: 3,
          code: 'SUPR',
          description: 'Supervisor',
          name: 'Supervisor',
          permissionValue: 8
        }
      ]
    }
  },

  watch: {
    // Fetch the user when the id param changes
    $route: {
      handler (route) {
        this.getUser(route.params.id)
      },
      immediate: true
    }
  },

  computed: {
    /** Determines if the user is a reviewer */
    isReviewer (): boolean {
      return this.user.permissions.findIndex(p => p.id === 1) !== -1
    },

    /** The user's availability sorted by appointment type */
    sortedAvailability (): Array<UserAvailabilityByType> {
      const availability = {} as { [id: number]: UserAvailabilityByType }

      this.user.availability.forEach(ua => {
        const av = cloneDeep(ua)
        delete av.appointmentTypeOffering.appointmentType
        const appointmentTypeId = ua.appointmentTypeOffering?.appointmentType?.id as number
        if (availability[appointmentTypeId]) {
          availability[appointmentTypeId].availability.push(av)
        } else {
          availability[appointmentTypeId] = ua.appointmentTypeOffering.appointmentType as UserAvailabilityByType
          availability[appointmentTypeId].availability = [av]
        }
      })

      return Object.values(availability)
    },

    /** The user's visibility permission */
    visilbilityPermission (): number {
      return this.editedPermissions.findIndex(p => p === 0) !== -1 ? 0 : 1
    }
  },

  methods: {
    /**
     * Creates a new blocked time
     */
    createBlockedTime () {
      this.isActive = false
      this.newBlockedTime.endTime = endOfDay(this.newBlockedTime.endTime)
      UserService.createBlockedTime(this.user.id, this.newBlockedTime).then(res => {
        this.user.blockedTimes.push(res.data)
        this.shouldShowBlockTimeform = false
        this.isActive = true
      })
    },

    // Edits the user's visibility permission
    editVisilbilityPermission (permissionId: number) {
      let ind
      if (permissionId === 0) {
        ind = this.editedPermissions.findIndex(p => p === 1)
      } else {
        ind = this.editedPermissions.findIndex(p => p === 0)
      }

      this.editedPermissions.splice(ind, 1)
      this.editedPermissions.push(permissionId)
    },

    /**
     * Formats a string represented a time in time format specified
     * @param dateString: The string representing a date
     * @param form: The output format
     */
    formatTimeString (dateString: string, form: string): string {
      const date = new Date(dateString)
      return format(date, form)
    },

    /**
     * Fetches the given user
     * @param id: The Id of the user
     */
    getUser (id: number) {
      UserService.getUser(id).then(res => {
        this.user = res.data
      })
    },

    /**
     * Handles a date range being selected setting the new block form object
     * with their values
     * @param start: The start date
     * @param end: The end date
     */
    handleDateSelectionn (start: Date, end: Date) {
      this.newBlockedTime.startTime = start
      this.newBlockedTime.endTime = end
    },

    /**
     * Removes the given blocked time
     * @parma blockedTime: The blocked time to remove
     */
    removeBlockedTime (id: number) {
      if (window.confirm('Are you sure?')) {
        this.$axios.put(`userBlockedTime/remove/${id}`).then(() => {
          const ind = this.user.blockedTimes.findIndex(bt => bt.id === id)
          this.user.blockedTimes.splice(ind, 1)
        })
      }
    },

    /**
     * Initializes a new blocked time and displays the form
     */
    showBlockedTimeForm () {
      this.newBlockedTime = {
        id: 0,
        title: '',
        startTime: null as unknown as Date,
        endTime: null as unknown as Date
      }

      this.shouldShowBlockTimeform = true

      // Show the datepicker
      setTimeout(() => {
        const picker = new Litepicker({ element: document.querySelector('.datepicker'), inlineMode: true, singleMode: false, numberOfMonths: 2, numberOfColumns: 2, onSelect: this.handleDateSelectionn })
        picker.show()
      }, 0)
    },

    /**
     * Resets the edited permission list and shows the permission form
     */
    showPermissionForm () {
      this.syncing = false
      this.editedPermissions = this.user.permissions.map(p => p.id)
      this.shouldShowPermissionForm = true
    },

    /**
     * Updates the user's availability
     * @param newAvailability: Updated availability
     */
    updateAvailability (newAvailability: Array<UserAvailability>) {
      this.user.availability = newAvailability
    },

    /**
     * Updates the user's permissions
     */
    updatePermissions () {
      this.syncing = true
      const permissionList = this.editedPermissions.map(up => this.permissions.find(p => p.id === up))
      UserService.updateUserPermissions(this.user.id, permissionList).then(res => {
        this.user.permissions = res.data.permissions
        this.shouldShowPermissionForm = false
      })
    }
  },

  components: {
    AvailabilityForm
  }
})
</script>
